/**
 * IconPicker — Shared icon + color selector
 *
 * Used across: Agent Builder, Skills, anywhere users pick an icon identity.
 * Features:
 * - 60 curated Lucide icons organized by category
 * - 12 color options with light/dark mode variants
 * - Search/filter icons
 * - Preview of selected icon + color
 * - Returns: { icon: string, color: string }
 */

import { useState, useMemo } from 'react';
import {
  // General
  Bot, Brain, Sparkles, Lightbulb, Target, Zap, Star, Heart, Flame, Eye,
  // Operations
  Wrench, Settings, Cog, Gauge, Timer, Activity, BarChart3, TrendingUp, PieChart, LineChart,
  // Safety & Compliance
  Shield, ShieldCheck, ShieldAlert, Lock, AlertTriangle, FileCheck, ClipboardCheck, BadgeCheck, Scale, Siren,
  // Documents & Knowledge
  FileText, BookOpen, Library, Notebook, ScrollText, FileSearch, BookMarked, GraduationCap, Microscope, Search,
  // Communication
  MessageSquare, Mail, Bell, Megaphone, Radio, Podcast, Send, MessagesSquare, AtSign, Globe,
  // Industrial
  Factory, HardHat, Thermometer, Droplets, Wind, Cpu, Database, Server, Plug, Cable,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

// ── Icon Registry ──────────────────────────────────────────────────────────

interface IconEntry {
  name: string;
  icon: LucideIcon;
  category: string;
  keywords: string[];
}

const ICON_REGISTRY: IconEntry[] = [
  // General
  { name: 'bot', icon: Bot, category: 'General', keywords: ['robot', 'ai', 'agent', 'assistant'] },
  { name: 'brain', icon: Brain, category: 'General', keywords: ['intelligence', 'thinking', 'smart'] },
  { name: 'sparkles', icon: Sparkles, category: 'General', keywords: ['magic', 'ai', 'generate', 'custom'] },
  { name: 'lightbulb', icon: Lightbulb, category: 'General', keywords: ['idea', 'insight', 'suggestion'] },
  { name: 'target', icon: Target, category: 'General', keywords: ['goal', 'focus', 'precision'] },
  { name: 'zap', icon: Zap, category: 'General', keywords: ['fast', 'lightning', 'event', 'trigger'] },
  { name: 'star', icon: Star, category: 'General', keywords: ['favorite', 'important', 'quality'] },
  { name: 'heart', icon: Heart, category: 'General', keywords: ['health', 'care', 'satisfaction'] },
  { name: 'flame', icon: Flame, category: 'General', keywords: ['hot', 'trending', 'urgent'] },
  { name: 'eye', icon: Eye, category: 'General', keywords: ['monitor', 'watch', 'observe', 'vision'] },

  // Operations
  { name: 'wrench', icon: Wrench, category: 'Operations', keywords: ['tool', 'maintenance', 'fix', 'repair'] },
  { name: 'settings', icon: Settings, category: 'Operations', keywords: ['config', 'gear', 'configure'] },
  { name: 'cog', icon: Cog, category: 'Operations', keywords: ['gear', 'process', 'mechanical'] },
  { name: 'gauge', icon: Gauge, category: 'Operations', keywords: ['meter', 'performance', 'speed'] },
  { name: 'timer', icon: Timer, category: 'Operations', keywords: ['time', 'schedule', 'countdown'] },
  { name: 'activity', icon: Activity, category: 'Operations', keywords: ['pulse', 'health', 'monitor', 'live'] },
  { name: 'bar-chart', icon: BarChart3, category: 'Operations', keywords: ['analytics', 'chart', 'data', 'report'] },
  { name: 'trending-up', icon: TrendingUp, category: 'Operations', keywords: ['growth', 'trend', 'increase'] },
  { name: 'pie-chart', icon: PieChart, category: 'Operations', keywords: ['distribution', 'breakdown'] },
  { name: 'line-chart', icon: LineChart, category: 'Operations', keywords: ['trend', 'time-series'] },

  // Safety & Compliance
  { name: 'shield', icon: Shield, category: 'Safety', keywords: ['security', 'protection', 'guard'] },
  { name: 'shield-check', icon: ShieldCheck, category: 'Safety', keywords: ['verified', 'approved', 'compliant'] },
  { name: 'shield-alert', icon: ShieldAlert, category: 'Safety', keywords: ['warning', 'risk', 'alert'] },
  { name: 'lock', icon: Lock, category: 'Safety', keywords: ['secure', 'private', 'restricted'] },
  { name: 'alert-triangle', icon: AlertTriangle, category: 'Safety', keywords: ['warning', 'caution', 'danger'] },
  { name: 'file-check', icon: FileCheck, category: 'Safety', keywords: ['audit', 'verified', 'checked'] },
  { name: 'clipboard-check', icon: ClipboardCheck, category: 'Safety', keywords: ['checklist', 'inspection'] },
  { name: 'badge-check', icon: BadgeCheck, category: 'Safety', keywords: ['certified', 'approved'] },
  { name: 'scale', icon: Scale, category: 'Safety', keywords: ['compliance', 'legal', 'regulation'] },
  { name: 'siren', icon: Siren, category: 'Safety', keywords: ['alarm', 'emergency', 'alert'] },

  // Documents & Knowledge
  { name: 'file-text', icon: FileText, category: 'Knowledge', keywords: ['document', 'text', 'file'] },
  { name: 'book-open', icon: BookOpen, category: 'Knowledge', keywords: ['manual', 'documentation', 'read'] },
  { name: 'library', icon: Library, category: 'Knowledge', keywords: ['collection', 'archive', 'knowledge'] },
  { name: 'notebook', icon: Notebook, category: 'Knowledge', keywords: ['notes', 'journal', 'log'] },
  { name: 'scroll-text', icon: ScrollText, category: 'Knowledge', keywords: ['sop', 'procedure', 'scroll'] },
  { name: 'file-search', icon: FileSearch, category: 'Knowledge', keywords: ['search', 'find', 'lookup'] },
  { name: 'book-marked', icon: BookMarked, category: 'Knowledge', keywords: ['bookmark', 'reference'] },
  { name: 'graduation-cap', icon: GraduationCap, category: 'Knowledge', keywords: ['training', 'learn', 'education'] },
  { name: 'microscope', icon: Microscope, category: 'Knowledge', keywords: ['analysis', 'research', 'inspect'] },
  { name: 'search', icon: Search, category: 'Knowledge', keywords: ['find', 'search', 'lookup', 'query'] },

  // Communication
  { name: 'message-square', icon: MessageSquare, category: 'Communication', keywords: ['chat', 'message', 'talk'] },
  { name: 'mail', icon: Mail, category: 'Communication', keywords: ['email', 'notification', 'send'] },
  { name: 'bell', icon: Bell, category: 'Communication', keywords: ['notification', 'alert', 'remind'] },
  { name: 'megaphone', icon: Megaphone, category: 'Communication', keywords: ['announce', 'broadcast'] },
  { name: 'radio', icon: Radio, category: 'Communication', keywords: ['broadcast', 'signal', 'wireless'] },
  { name: 'podcast', icon: Podcast, category: 'Communication', keywords: ['audio', 'broadcast', 'voice'] },
  { name: 'send', icon: Send, category: 'Communication', keywords: ['submit', 'dispatch', 'deliver'] },
  { name: 'messages-square', icon: MessagesSquare, category: 'Communication', keywords: ['discussion', 'thread'] },
  { name: 'at-sign', icon: AtSign, category: 'Communication', keywords: ['mention', 'email', 'contact'] },
  { name: 'globe', icon: Globe, category: 'Communication', keywords: ['web', 'internet', 'global'] },

  // Industrial
  { name: 'factory', icon: Factory, category: 'Industrial', keywords: ['plant', 'manufacturing', 'production'] },
  { name: 'hard-hat', icon: HardHat, category: 'Industrial', keywords: ['safety', 'construction', 'ppe'] },
  { name: 'thermometer', icon: Thermometer, category: 'Industrial', keywords: ['temperature', 'heat', 'sensor'] },
  { name: 'droplets', icon: Droplets, category: 'Industrial', keywords: ['water', 'fluid', 'chemical'] },
  { name: 'wind', icon: Wind, category: 'Industrial', keywords: ['air', 'ventilation', 'hvac'] },
  { name: 'cpu', icon: Cpu, category: 'Industrial', keywords: ['processor', 'compute', 'plc'] },
  { name: 'database', icon: Database, category: 'Industrial', keywords: ['data', 'storage', 'historian'] },
  { name: 'server', icon: Server, category: 'Industrial', keywords: ['machine', 'system', 'infrastructure'] },
  { name: 'plug', icon: Plug, category: 'Industrial', keywords: ['connection', 'power', 'integrate'] },
  { name: 'cable', icon: Cable, category: 'Industrial', keywords: ['wire', 'network', 'connect'] },
];

// ── Color Palette ──────────────────────────────────────────────────────────

export interface IconColor {
  name: string;
  light: string;   // Light mode: icon color
  dark: string;     // Dark mode: icon color
  bg: string;       // Background (works in both modes)
}

export const ICON_COLORS: IconColor[] = [
  { name: 'blue', light: 'text-blue-600', dark: 'dark:text-blue-400', bg: 'bg-blue-500/10' },
  { name: 'violet', light: 'text-violet-600', dark: 'dark:text-violet-400', bg: 'bg-violet-500/10' },
  { name: 'emerald', light: 'text-emerald-600', dark: 'dark:text-emerald-400', bg: 'bg-emerald-500/10' },
  { name: 'amber', light: 'text-amber-600', dark: 'dark:text-amber-400', bg: 'bg-amber-500/10' },
  { name: 'red', light: 'text-red-600', dark: 'dark:text-red-400', bg: 'bg-red-500/10' },
  { name: 'cyan', light: 'text-cyan-600', dark: 'dark:text-cyan-400', bg: 'bg-cyan-500/10' },
  { name: 'pink', light: 'text-pink-600', dark: 'dark:text-pink-400', bg: 'bg-pink-500/10' },
  { name: 'orange', light: 'text-orange-600', dark: 'dark:text-orange-400', bg: 'bg-orange-500/10' },
  { name: 'indigo', light: 'text-indigo-600', dark: 'dark:text-indigo-400', bg: 'bg-indigo-500/10' },
  { name: 'teal', light: 'text-teal-600', dark: 'dark:text-teal-400', bg: 'bg-teal-500/10' },
  { name: 'rose', light: 'text-rose-600', dark: 'dark:text-rose-400', bg: 'bg-rose-500/10' },
  { name: 'zinc', light: 'text-zinc-600', dark: 'dark:text-zinc-400', bg: 'bg-zinc-500/10' },
];

// Solid background colors for the color picker swatches (fully visible in both modes)
const SOLID_COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-500',
  violet: 'bg-violet-500',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  cyan: 'bg-cyan-500',
  pink: 'bg-pink-500',
  orange: 'bg-orange-500',
  indigo: 'bg-indigo-500',
  teal: 'bg-teal-500',
  rose: 'bg-rose-500',
  zinc: 'bg-zinc-500',
};

// ── Helper: Get icon component by name ─────────────────────────────────────

export function getIconByName(name: string): LucideIcon | null {
  return ICON_REGISTRY.find(e => e.name === name)?.icon ?? null;
}

export function getColorByName(name: string): IconColor {
  return ICON_COLORS.find(c => c.name === name) ?? ICON_COLORS[0];
}

// ── Rendered Icon with Color ───────────────────────────────────────────────

export function AgentIcon({ iconName, colorName, size = 'md', className }: {
  iconName: string;
  colorName: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const IconComponent = getIconByName(iconName) ?? Bot;
  const color = getColorByName(colorName);

  const sizes = { sm: 'w-7 h-7', md: 'w-9 h-9', lg: 'w-12 h-12' };
  const iconSizes = { sm: 'h-3.5 w-3.5', md: 'h-4.5 w-4.5', lg: 'h-6 w-6' };

  return (
    <div className={cn('rounded-xl flex items-center justify-center shrink-0', sizes[size], color.bg, className)}>
      <IconComponent className={cn(iconSizes[size], color.light, color.dark)} />
    </div>
  );
}

// ── Icon Picker Component ──────────────────────────────────────────────────

export interface IconPickerValue {
  icon: string;
  color: string;
}

interface IconPickerProps {
  value: IconPickerValue;
  onChange: (value: IconPickerValue) => void;
  className?: string;
}

export function IconPicker({ value, onChange, className }: IconPickerProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const categories = useMemo(() => {
    const cats = new Set(ICON_REGISTRY.map(e => e.category));
    return Array.from(cats);
  }, []);

  const filtered = useMemo(() => {
    let icons = ICON_REGISTRY;
    if (activeCategory) icons = icons.filter(e => e.category === activeCategory);
    if (search) {
      const q = search.toLowerCase();
      icons = icons.filter(e => e.name.includes(q) || e.keywords.some(k => k.includes(q)) || e.category.toLowerCase().includes(q));
    }
    return icons;
  }, [search, activeCategory]);

  const selectedColor = getColorByName(value.color);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Preview */}
      <div className="flex items-center gap-3">
        <AgentIcon iconName={value.icon} colorName={value.color} size="lg" />
        <div>
          <p className="text-xs font-medium">{value.icon}</p>
          <p className="text-[10px] text-muted-foreground">{value.color}</p>
        </div>
      </div>

      {/* Color palette */}
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Color</p>
        <div className="flex flex-wrap gap-1.5">
          {ICON_COLORS.map(color => (
            <button
              key={color.name}
              onClick={() => onChange({ ...value, color: color.name })}
              className={cn(
                'w-7 h-7 rounded-full transition-all',
                SOLID_COLOR_MAP[color.name],
                value.color === color.name ? 'ring-2 ring-primary ring-offset-2 ring-offset-background scale-110' : 'hover:scale-110'
              )}
              title={color.name}
            />
          ))}
        </div>
      </div>

      {/* Search + Category filter */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1.5 h-3 w-3 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search icons..." className="h-6 text-[10px] pl-6" />
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-1">
        <button
          onClick={() => setActiveCategory(null)}
          className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors', !activeCategory ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors', activeCategory === cat ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Icon grid */}
      <div className="grid grid-cols-8 gap-1 max-h-[160px] overflow-auto">
        {filtered.map(entry => {
          const Icon = entry.icon;
          const isSelected = value.icon === entry.name;
          return (
            <button
              key={entry.name}
              onClick={() => onChange({ ...value, icon: entry.name })}
              className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center transition-all',
                isSelected ? cn(selectedColor.bg, 'ring-1 ring-primary/40') : 'hover:bg-muted'
              )}
              title={entry.name}
            >
              <Icon className={cn('h-4 w-4', isSelected ? cn(selectedColor.light, selectedColor.dark) : 'text-muted-foreground')} />
            </button>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-2">No icons matching "{search}"</p>
      )}
    </div>
  );
}
